import { useEffect, useMemo } from 'react';
import { Portal } from '@aviv-estimation-funnel/estimation-data-models/enums';
import { Box } from '@gemini/core';
import { CustomEventService, IEvents } from '@mfe-helpers/customEvent';
import { stepComponent } from '@mfe-helpers/fieldComponent';
import { composeInteractionPayload, composeMPUInteractionPayload } from '@mfe-helpers/tracking';
import { reactHookFormResolver } from '@mfe-presentation//hooks/reactHookFormResolver';
import Headline from '@mfe-presentation/components/Headline';
import LegacyUpdateMessage from '@mfe-presentation/components/LegacyUpdateMessage';
import FieldsetWrapper from '@mfe-presentation/components/Wrappers/FieldsetWrapper/FieldsetWrapper';
import FieldWrapper from '@mfe-presentation/components/Wrappers/FieldWrapper';
import { useAppContext, useAppDispatch } from '@mfe-presentation/contexts';
import useNavigation from '@mfe-presentation/hooks/useNavigation';
import useSendPageView from '@mfe-presentation/hooks/useSendPageView';
import useSetDatalayer from '@mfe-presentation/hooks/useSetDatalayer';
import { EActionType } from '@mfe-types/context';
import {
    EAvCategory,
    EInteractionEvents,
    EMonthlyPriceUpdateInteractionEvents,
} from '@mfe-types/tracking';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { progressHeightDesktop, topbarHeight } from '@mfe-presentation/constants/layout';
import { useTranslation } from '@aviv-unified-frontend/runtime/i18n';
import { NAME_SPACE } from '@mfe-presentation/hooks/useLoadTranslations';
import { tExists } from '@aviv-estimation-funnel/shells/src/app/helpers/i18n';
import Navigation from '@mfe-presentation/components/Navigation';

const Form = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    // The data layer should be set before any other pageview event
    useSetDatalayer();
    useSendPageView();

    const {
        config,
        formValues,
        currentStep: { name },
        portal,
        isFromUpdatePage,
    } = useAppContext();
    const { t } = useTranslation(NAME_SPACE);
    const urlParams = new URLSearchParams(window.location.search);
    const hasLegacyParam = urlParams.get('legacy');
    const isFromImmowebLegacyUpdatePage =
        isFromUpdatePage && hasLegacyParam && portal === Portal.ImmoWeb;

    const currentFields = useMemo(() => {
        return config[name].getFields(formValues);
    }, [config[name]]);

    const currentFieldnamesWithFieldsets = useMemo(() => {
        const results: Record<string, Array<string>> = {};
        for (const [fieldKey, field] of Object.entries(currentFields)) {
            if (field.parent) {
                if (!results[field.parent]) {
                    results[field.parent] = [];
                }
                results[field.parent].push(fieldKey);
            } else {
                results[fieldKey] = [fieldKey];
            }
        }
        return results;
    }, [currentFields]);

    const methods = useForm({
        mode: 'all',
        defaultValues: formValues,
        resolver: async (values, _, options) =>
            reactHookFormResolver(values, currentFields, options),
    });

    const { handleNavigation, handleBack } = useNavigation();

    const onSubmit = async (formData: Record<string, any>) => {
        dispatch({
            type: EActionType.SetStepValues,
            payload: formData,
        });

        if (name === 'identity') {
            CustomEventService.fire(
                IEvents.SEND_TRACKING_EVENT,
                composeInteractionPayload({
                    eventName: EInteractionEvents.LeadSubmit,
                    formValues,
                    portal,
                }),
            );
        }

        if (name === 'update') {
            CustomEventService.fire(
                IEvents.SEND_TRACKING_EVENT,
                composeMPUInteractionPayload({
                    eventName: EMonthlyPriceUpdateInteractionEvents.LeadSubmit,
                    formValues,
                    category: EAvCategory.EstimationFunnel,
                    portal,
                }),
            );
        }
        handleNavigation('next', formData);
    };

    const focusAndScrollOnNextField = () => {
        for (const fieldKey of Object.keys(methods.formState.errors || {})) {
            const elementByName = document.getElementsByName(
                fieldKey,
            )[0] as HTMLInputElement | null;
            const elementById = document.getElementById(fieldKey) as HTMLInputElement | null;
            const element =
                elementByName && elementByName?.type !== 'hidden' ? elementByName : elementById;
            if (element) {
                element.focus();
                const topBarOffset = topbarHeight + progressHeightDesktop;
                const labelElement = element?.labels?.[0];
                const elementPosition = labelElement
                    ? labelElement.getBoundingClientRect()?.top
                    : element.getBoundingClientRect()?.top;
                const topPosition = elementPosition + window.scrollY - topBarOffset;
                window.scrollTo({ top: topPosition, behavior: 'smooth' });
                break;
            }
        }
    };

    const handleSubmit = (e: any) => {
        e.preventDefault();
        dispatch({
            type: EActionType.SetHasSubmittedCurrentStep,
            payload: true,
        });
        if (!methods.formState.isValid) {
            // hotfix: ensure methods.formState.errors is up to date
            window.setTimeout(() => {
                focusAndScrollOnNextField();
            }, 150);
        }
        methods.handleSubmit(onSubmit)();
    };

    const configComponent = config[name].stepComponent;
    const Step = configComponent ? stepComponent(configComponent) : null;

    useEffect(() => {
        // force the validation of the form when the step changes except for Identity step where there is specific validation rules
        if (name !== 'identity') {
            methods.trigger();
        }

        // avoid mismatched between current step and url
        if (!window.location.pathname.endsWith(name)) {
            // TODO improve general navigation
            // This is a quick fix to prevent a glitch when navigating back with browser or swipe gesture
            if (name === 'loading') {
                dispatch({
                    type: EActionType.SetCurrentStep,
                    payload: { name: 'address', index: 0 },
                });
                navigate(`../address${window.location.search}`);
            } else navigate(`../${name}${window.location.search}`);
        }
    }, [name]);

    return (
        <FormProvider {...methods}>
            {isFromImmowebLegacyUpdatePage && <LegacyUpdateMessage />}
            <Headline />
            <Box
                as="form"
                display="flex"
                justifyContent="center"
                flexDirection="column"
                width="100%"
                onSubmit={handleSubmit}
                testId="form"
                id="form"
            >
                <Box
                    height="100%"
                    width="100%"
                    marginTop="spacing.none"
                    marginHorizontal="auto"
                    marginBottom="auto"
                    maxWidth="320px"
                >
                    {Step ? (
                        <Step />
                    ) : (
                        Object.keys(currentFieldnamesWithFieldsets).map((key) => {
                            const fieldsetFieldnames = currentFieldnamesWithFieldsets[key];
                            const fields = fieldsetFieldnames.map((fieldKey) => (
                                <FieldWrapper
                                    key={fieldKey}
                                    name={fieldKey}
                                    field={currentFields[fieldKey]}
                                />
                            ));
                            if (fieldsetFieldnames.length > 1) {
                                return (
                                    <FieldsetWrapper
                                        key={key}
                                        name={key}
                                        legendTitle={
                                            tExists(`step_benefits.fieldsets.${key}.legend`) ?? ''
                                        }
                                    >
                                        {fields}
                                    </FieldsetWrapper>
                                );
                            }
                            return fields;
                        })
                    )}
                    <Navigation handleClickBackButton={handleBack} />
                </Box>
            </Box>
        </FormProvider>
    );
};

export default Form;
